import { Link } from "wouter";
import { useBookCta } from "@/hooks/use-book-href";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { PageHero } from "@/components/layout/page-hero";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { HouseRulesDisplay, useHouseRules } from "@/components/legal/house-rules-display";
import { wavesVideo, parallaxBg, wavesPoster } from "@/lib/media-assets";
import {
  useSEO,
  useSEOSettings,
  generateBreadcrumbSchema,
  generateSpeakableSchema,
} from "@/lib/seo-utils";

/**
 * The canonical House Rules page.
 *
 * Everything that needs to show or link the rules points here: the policies
 * page, the booking flow, confirmation and pre-arrival emails, the guest portal
 * and the admin reservation record. One URL, one published version, so a guest
 * reading the rules from an email and a guest reading them on the site are
 * reading the same words.
 */
export default function HouseRulesPage() {
  const { seoSettings } = useSEOSettings();
  const { data } = useHouseRules();
  const bookCta = useBookCta();

  const seoHead = useSEO({
    pageType: "house-rules",
    title: "House Rules | The Kualoa Beach House, Oahu",
    description:
      "The house rules for The Kualoa Beach House in Ka'a'awa, Oahu: check-in and checkout, occupancy, quiet hours, pest and power-outage disclosures, trash separation, the departure checklist and the water equipment waiver.",
    canonical: "https://vacationrentaloahu.co/house-rules",
    ogType: "website",
    twitterCard: "summary_large_image",
    structuredData: seoSettings
      ? [
          generateBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "House Rules", url: "/house-rules" },
          ]),
          generateSpeakableSchema(["h1", "h2"]),
        ]
      : undefined,
  });

  return (
    <>
      {seoHead}
      <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
        <Header />
        <main id="main-content">
          <PageHero
            eyebrow="Before you stay"
            title="House Rules"
            subtitle="What we ask of every guest, and what you should know about the property before you book."
            videoSrc={wavesVideo}
            posterSrc={wavesPoster}
            imageAlt="Beachfront ocean views at The Kualoa Beach House in Ka'a'awa, Oahu"
          />

          <section className="py-16 md:py-24 bg-shell">
            <div className="container mx-auto px-6 max-w-3xl">
              <Breadcrumbs
                items={[
                  { name: "Home", url: "/" },
                  { name: "House Rules", url: "/house-rules" },
                ]}
                className="mb-12 justify-center"
              />

              {/* Jump links. The rules are long by necessity; a reader looking for
                  the bin colours should not have to scroll the pest disclosure. */}
              {data && data.sections.length > 3 && (
                <ScrollReveal>
                  <nav aria-label="Jump to a section" className="mb-14 border border-sand-deep bg-sand p-6">
                    <p className="text-[11px] font-sans font-semibold uppercase tracking-[0.2em] text-driftwood mb-4">
                      Jump to
                    </p>
                    <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                      {data.sections.map((s) => (
                        <li key={s.heading}>
                          <a
                            href={`#${s.anchor ?? ""}`}
                            className="inline-flex min-h-[24px] items-center text-[15px] text-ink-soft font-sans hover:text-ink border-b border-transparent hover:border-sand-deep transition-colors"
                          >
                            {s.heading}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </ScrollReveal>
              )}

              <HouseRulesDisplay variant="full" />

              <div className="mt-14 pt-10 border-t border-sand-deep text-center">
                <p className="text-luxury-lg text-ink-soft leading-relaxed mb-6">
                  Questions about any of this?{" "}
                  <Link href="/contact" className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">Ask us</Link>
                  {" "}before you book. You can also read our{" "}
                  <Link href="/policies" className="text-ink border-b border-sand-deep hover:border-sun transition-colors duration-300">cancellation policy and terms</Link>.
                </p>
                <Link
                  href={bookCta.href}
                  data-primary-book-cta
                  className="inline-flex items-center justify-center bg-sun text-shell hover:bg-sun-hover font-sans tracking-wide px-8 py-4 transition-colors duration-300"
                >
                  Check availability
                </Link>
              </div>
            </div>
          </section>
        </main>
        <StickyMobileBookCta />
      <Footer />
      </div>
    </>
  );
}
